/** Общие запросы данных (TanStack Query). Ключи: ["post", id], ["posts", …], ["projects", …], ["settings"], ["vk"]. */
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { PostFull, Project, PublicSettings, VkStatus } from "@impuls/shared";
import { api } from "./api";
import { setDisplayTimeZone } from "./format";

export const usePost = (id: number) =>
  useQuery({ queryKey: ["post", id], queryFn: () => api.get<PostFull>(`/api/posts/${id}`), enabled: Number.isFinite(id) && id > 0 });

export const useSettings = (enabled = true) =>
  useQuery({ queryKey: ["settings"], queryFn: () => api.get<PublicSettings>("/api/settings"), staleTime: 30_000, enabled });

/** Пояс показа времени = settings.timezone: и после загрузки, и после сохранения (setQueryData). */
export function syncDisplayTimeZone(qc: QueryClient) {
  const apply = (s: Partial<PublicSettings> | undefined) => {
    if (s?.timezone) setDisplayTimeZone(s.timezone);
  };
  apply(qc.getQueryData<PublicSettings>(["settings"]));
  return qc.getQueryCache().subscribe((e) => {
    if (e.type === "updated" && e.query.queryKey[0] === "settings") apply(e.query.state.data as Partial<PublicSettings> | undefined);
  });
}

export const useProjects = (archived: "0" | "1" | "all" = "0") =>
  useQuery({ queryKey: ["projects", archived], queryFn: () => api.get<Project[]>(`/api/projects?archived=${archived}`) });

export const useVkStatus = (enabled = true) =>
  useQuery({ queryKey: ["vk"], queryFn: () => api.get<VkStatus>("/api/vk/check"), staleTime: 60_000, enabled, retry: false });

/** Обновить всё, что зависит от постов (списки, календарь, счётчики). */
export function useRefreshPosts() {
  const qc = useQueryClient();
  return (id?: number) => {
    if (id) void qc.invalidateQueries({ queryKey: ["post", id] });
    void qc.invalidateQueries({ queryKey: ["posts"] });
    void qc.invalidateQueries({ queryKey: ["calendar"] });
    void qc.invalidateQueries({ queryKey: ["stats"] });
    void qc.invalidateQueries({ queryKey: ["projects"] });
  };
}
